import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ArrowsClockwise,
  Plus,
  Folder,
  MagnifyingGlass,
  Tray,
  FloppyDisk,
} from '@phosphor-icons/react';
import { ThemeCard } from './ThemeCard';
import { FolderCard } from './FolderCard';
import type { DocumentNodeWithDetailsDto, UserCompanyRole } from '@monorepo/shared';
import { hasPermission, Permission } from '@monorepo/shared';

interface DocumentsRootViewProps {
  companyId: string;
  folders: DocumentNodeWithDetailsDto[];
  totalCount: number;
  isLoading: boolean;
  currentUserRole?: UserCompanyRole | undefined;
  isAtRoot: boolean;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRefresh: () => void;
  onAddFolder: () => void;
  onOpenFolder: (nodeId: string) => void;
  onRename: (node: DocumentNodeWithDetailsDto) => void;
  onDelete: (node: DocumentNodeWithDetailsDto) => void;
}

export const DocumentsRootView = ({
  companyId,
  folders,
  totalCount,
  isLoading,
  currentUserRole,
  isAtRoot,
  searchQuery,
  onSearchChange,
  onRefresh,
  onAddFolder,
  onOpenFolder,
  onRename,
  onDelete,
}: DocumentsRootViewProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const canViewLatestVersions = currentUserRole
    ? hasPermission(currentUserRole, Permission.UPLOAD_NEW_VERSION)
    : false;
  const canCreateTheme = currentUserRole
    ? hasPermission(currentUserRole, Permission.CREATE_THEME)
    : false;
  const canRenameTheme = currentUserRole
    ? hasPermission(currentUserRole, Permission.RENAME_THEME)
    : false;
  const canDeleteTheme = currentUserRole
    ? hasPermission(currentUserRole, Permission.DELETE_THEME)
    : false;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t('documents.title')}
            <span className="ml-2 text-base font-normal text-gray-500">
              ({t('documents.subtitle', { count: totalCount })})
            </span>
          </h1>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={onRefresh}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <ArrowsClockwise size={18} />
            {t('documents.refresh')}
          </button>

          {canCreateTheme && (
            <button
              onClick={onAddFolder}
              className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-primary rounded-lg hover:bg-opacity-90 transition"
            >
              <Plus size={18} />
              {t('documents.add_folder')}
            </button>
          )}
        </div>
      </div>

      <div className="mb-6 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('documents.search_placeholder')}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        <div className="flex items-center gap-3 md:ml-auto">
          {canViewLatestVersions && (
            <button
              onClick={() => navigate('/documents/latest')}
              className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              <FloppyDisk size={18} />
              {t('documents.latest_versions_button')}
            </button>
          )}
          <button
            onClick={() => navigate('/documents/archived')}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
          >
            <Tray size={18} />
            {t('documents.archived_button')}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin">
            <ArrowsClockwise size={32} className="text-primary" />
          </div>
        </div>
      ) : folders.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Folder size={64} className="text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('documents.empty.title')}</h3>
          <p className="text-gray-500 max-w-md">{t('documents.empty.description')}</p>
        </div>
      ) : isAtRoot ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-stretch">
          {folders.map((node) => (
            <ThemeCard
              key={node.id}
              node={node}
              companyId={companyId}
              canRename={canRenameTheme}
              canDelete={canDeleteTheme}
              onOpen={onOpenFolder}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {folders.map((node) => (
            <FolderCard
              key={node.id}
              node={node}
              childrenCount={0}
              onOpen={onOpenFolder}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
};
